// WHAT: TracQ-branded roadmap item card component using global UI components
// WHY:  Provides a consistent branded card component for displaying roadmap items
// DOMAIN: Admin → Roadmap → Overview
// DEPENDENCIES: Card, Badge, Button from @/components/ui, roadmapMapping utilities
// RISKS:
//   - NOTE: Tags are limited to 3 visible - remaining shown as count
//   - NOTE: Date formatting uses 'de-DE' locale - may not match user preference
//   - NOTE: Component is memoized - ensure props are stable

import {
  LucideBug,
  LucideLightbulb,
  LucideSparkles,
  LucideTarget,
  LucideTrash2,
  LucideEdit3,
  LucideClock,
  LucideCheckCircle,
  LucideXCircle,
  LucideZap,
} from "lucide-react";
import React, { memo, useMemo } from "react";

import { Card, Badge, Button } from "@/components/ui";
import {
  type Roadmap_Item,
  type Roadmap_Item_Type,
} from "@/domains/admin/roadmap/types/roadmap-types";
import { typeToString, scoreLevelToString } from "@/domains/admin/roadmap/utils/roadmapMapping";

/**
 * Props for RoadmapItemCardTracQ component
 */
export interface RoadmapItemCardProperties {
  /** Roadmap item to display */
  item: Roadmap_Item;
  /** Callback when delete button is clicked */
  onDelete: (item: Roadmap_Item) => void;
  /** Callback when edit button is clicked */
  onEdit: (item: Roadmap_Item) => void;
}

/**
 * WHAT:
 *   TracQ-branded roadmap item card component using global UI components
 *   for consistent styling and branding.
 *
 * WHY:
 *   Provides a standardized card component that follows TracQ design system
 *   and uses shared UI components for maintainability.
 *
 * PROPS:
 *   - item: Roadmap_Item — Roadmap item to display
 *   - onEdit: (item: Roadmap_Item) => void — Handler for edit action
 *   - onDelete: (item: Roadmap_Item) => void — Handler for delete action
 *
 * BEHAVIOR:
 *   - Displays item type icon, title, description, module
 *   - Shows status icon and priority indicator
 *   - Displays progress bar if progress is defined
 *   - Shows impact and effort badges
 *   - Limits visible tags to 3, shows count for remaining
 *   - Formats updated date in German locale
 *   - Provides edit and delete buttons
 *
 * RISKS:
 *   - NOTE: Tags limited to 3 visible - users may not see all tags
 *   - NOTE: Date formatting hardcoded to 'de-DE' - consider user locale preference
 *   - NOTE: Component is memoized - ensure item object reference is stable
 *
 * @param root0
 *
 * @param root0.item
 *
 * @param root0.onEdit
 *
 * @param root0.onDelete
 *
 * @returns {JSX.Element} TracQ-branded roadmap item card component
 *
 */
const RoadmapItemCardTracQ: React.FC<RoadmapItemCardProperties> = ({ item, onDelete, onEdit }) => {
  const typeIcon = useMemo(() => {
    const iconMap: Record<Roadmap_Item_Type, React.ReactElement> = {
      bug: <LucideBug className="w-5 h-5 text-red-500" />,
      feature: <LucideTarget className="w-5 h-5 text-tracq-bright-blue" />,
      idea: <LucideLightbulb className="w-5 h-5 text-yellow-500" />,
      insight: <LucideSparkles className="w-5 h-5 text-green-500" />,
    };
    return iconMap[item.type];
  }, [item.type]);

  const statusIcon = useMemo(() => {
    const statusMap: Record<string, React.ReactElement> = {
      done: <LucideCheckCircle className="w-4 h-4 text-green-500" />,
      in_progress: <LucideZap className="w-4 h-4 text-tracq-bright-blue" />,
      open: <LucideClock className="w-4 h-4 text-slate-400" />,
      rejected: <LucideXCircle className="w-4 h-4 text-red-500" />,
    };
    const status = typeof item.status === "string" ? item.status : "open";
    return statusMap[status] ?? statusMap.open;
  }, [item.status]);

  const priorityColor = useMemo(() => {
    const colorMap: Record<string, string> = {
      high: "bg-tracq-deep-orange",
      low: "bg-green-500",
      medium: "bg-yellow-500",
    };
    const priority = typeof item.priority === "string" ? item.priority : "medium";
    return colorMap[priority] ?? colorMap.medium;
  }, [item.priority]);

  const formattedDate = useMemo(
    () =>
      new Date(item.updated_at).toLocaleDateString("de-DE", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
    [item.updated_at],
  );

  const handleEdit = () => {
    onEdit(item);
  };
  const handleDelete = () => {
    onDelete(item);
  };

  return (
    <Card
      className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card hover:border-slate-500/50 transition-all duration-300 hover:scale-[1.02] min-h-[320px] flex flex-col"
      padding="lg"
      variant="default"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-slate-600/50">{typeIcon}</div>
          <div>
            <Badge className="text-xs" variant="outline">
              {typeToString(item.type)}
            </Badge>
            <div className="text-xs text-slate-400 mt-1">{item.module}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {statusIcon}
          <div className={`w-2 h-2 rounded-full ${priorityColor ?? ""}`} />
        </div>
      </div>

      {/* Title */}
      <h3 className="text-white font-semibold text-lg leading-tight mb-3 hover:text-tracq-bright-blue transition-colors duration-200 cursor-pointer">
        {item.title}
      </h3>

      {/* Description */}
      {item.description ? (
        <p className="text-sm text-slate-400 leading-relaxed mb-4 line-clamp-3">
          {item.description}
        </p>
      ) : null}

      {/* Progress */}
      {item.progress !== undefined && (
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-300">Progress</span>
            <span className="text-tracq-bright-blue font-medium">{item.progress}%</span>
          </div>
          <div className="w-full bg-slate-600 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-tracq-bright-blue to-tracq-deep-blue h-2 rounded-full transition-all duration-500"
              style={{ width: `${String(item.progress)}%` }}
            />
          </div>
        </div>
      )}

      {/* Scores */}
      <div className="flex gap-2 mb-4">
        <Badge className="text-xs" variant="outline">
          Impact: {scoreLevelToString(item.impact_score)}
        </Badge>
        <Badge className="text-xs" variant="outline">
          Effort: {scoreLevelToString(item.effort_estimate)}
        </Badge>
      </div>

      {/* Tags */}
      {item.tags && item.tags.length > 0 ? (
        <div className="flex flex-wrap gap-1 mb-4">
          {item.tags.slice(0, 3).map((tag) => (
            <Badge className="text-xs" key={tag} variant="outline">
              {tag}
            </Badge>
          ))}
          {item.tags.length > 3 && (
            <Badge className="text-xs" variant="outline">
              +{item.tags.length - 3}
            </Badge>
          )}
        </div>
      ) : null}

      {/* Footer */}
      <div className="mt-auto pt-4 border-t border-slate-600/50">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Updated {formattedDate}</span>
          <div className="flex gap-2">
            <Button className="p-1 h-8 w-8" onClick={handleEdit} size="sm" variant="ghost">
              <LucideEdit3 className="w-4 h-4" />
            </Button>
            <Button
              className="p-1 h-8 w-8 text-red-500 hover:text-red-400"
              onClick={handleDelete}
              size="sm"
              variant="ghost"
            >
              <LucideTrash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export const RoadmapItemCardTracQMemo = memo(RoadmapItemCardTracQ);
